/**
 * `/sale/*` routes v2 calls (API_ACCESS.md §3.1). Each access is the backend's, held to the
 * snapshot by `api/routes.test.ts`. Moved here from `ordering/commit/contract.ts`'s `SALE_ROUTES`
 * (one definition, API0).
 */
import { gated } from "../../capabilities/access";
import { defineRoutes, route } from "../route";

/** one table's live state, as `sale/resource/branch/current` answers it; `currentStatus` is `Free` or `Occupied` */
export interface WireTableOccupancy {
  /** the table's id */
  resourceId: string;
  /** `Occupied` while the table has an open order, else `Free` */
  currentStatus: string;
  /** the open orders sitting on the table */
  orders?: Record<string, unknown>[];
  /** the one upcoming booking of the table, when there is one; v2 does not read it (see the route's notes) */
  reservation?: Record<string, unknown>;
  /** the one booking running on the table now, only when asked for with `includeRunning`; v2 does not read it */
  runningReservation?: Record<string, unknown>;
}

/** a sale as the routes post and answer it; the commit pipeline's types narrow it */
type SaleBody = Record<string, unknown>;
type SaleAnswer = Record<string, unknown>;

export const SALE = defineRoutes("sale", {
  save: route<SaleBody, SaleAnswer>("POST", "/sale/save", gated({ modules: ["Sale", "Order", "Kiosk"] }), {
    notes:
      "the handler also asks CREATE_NEW_ORDER for a new sale and BACK_ORDER_FEATURE with back-order items, and refuses a sale of another branch than the token's (saletxn_controller.js:434-446; saletxn_service.js:1698-1700)",
  }),
  print: route<SaleBody, SaleAnswer>("POST", "/sale/print", gated({ modules: ["Sale", "Order", "Kiosk", "Support"] }), {
    notes:
      "an Open sale only; it saves first, so the save's checks apply, and for any licence but SPT a Created or Confirmed online sale becomes an Accept (saletxn_controller.js:452-456, :1797-1826; POS_VS_SUPPORT_ORDERING.md §8.4)",
  }),
  printClosed: route<SaleBody, SaleAnswer, { saleId: string }>(
    "POST",
    "/sale/:saleId/print",
    gated({ modules: ["Sale", "Order", "Delivery"] }),
    { notes: "a Closed sale's reprint; INVOICE_REPRINT once the sale has a print log (sale_service.js:4116)" },
  ),
  discountUpdate: route<SaleBody, SaleAnswer, { saleId: string }>(
    "POST",
    "/sale/:saleId/discount/update",
    gated({ modules: ["Sale", "Order"] }),
    { notes: "a Closed sale's discounts, saved by the discount module (`useDiscountApproval`'s save and approval request)" },
  ),
  customerOffers: route<SaleBody, SaleAnswer>("POST", "/sale/customer/offers", gated({ modules: ["Sale", "Order", "Support"] }), {
    notes:
      "the partner offers: the offers of every offers provider for the sale's channel, whatever provider the body names, which is used only for its consent check (sale_customer_service.js:505-606)",
  }),
  customerLoyaltyRedeem: route<SaleBody, SaleAnswer>("POST", "/sale/customer/loyalty/redeem", gated({ modules: ["Sale", "Order"] }), {
    notes: "a partner offer's apply, and Xeno's OTP verify (the partner port, `promotions/partners/port.ts` › `requestApply`, `verify`)",
  }),
  customerLoyalty: route<SaleBody, SaleAnswer>("POST", "/sale/customer/loyalty", gated({ modules: ["Sale", "Order", "Support"] }), {
    notes:
      "a loyalty partner's points details for the sale's customer: the partner's answer as is, or REST_RESTRICTED_ACTION when the customer declined consent; needs `sale` and `providerId`, takes `fetch` (sale_loyalty_service.js:21-122; points-conversations.md §1)",
  }),
  customerLoyaltyVerify: route<SaleBody, SaleAnswer>("POST", "/sale/customer/loyalty/verify", gated({ modules: ["Sale", "Order"] }), {
    notes:
      "a points redemption's OTP verify: needs `sale`, `loyaltyInput` and `providerId`; the backend writes the points line onto the sale here and nowhere else (sale_loyalty_service.js:282-392; points-conversations.md §1, §3)",
  }),
  customerLoyaltyRefund: route<SaleBody, SaleAnswer>("POST", "/sale/customer/loyalty/refund", gated({ modules: ["Sale", "Order"] }), {
    notes:
      "returns the points on the sale's own points line to the customer; needs `sale` and `providerId`; refused on a Closed sale (saletxn_controller.js:2369-2412; sale_loyalty_service.js:404-569)",
  }),
  customerPointsRelease: route<SaleBody, SaleAnswer, { type: string }>("POST", "/sale/customer/:type/release", gated({ modules: ["Sale", "Order"] }), {
    notes:
      "asks the partner to release a hold on points: the handler never reads `:type`, and calls the partner's refund with the body (`loyaltyProvider.metaId`, `sale`); refused on a Closed sale (saletxn_controller.js:2414-2441)",
  }),
  customerOfferRedeem: route<SaleBody, SaleAnswer>("POST", "/sale/customer/offer/redeem", gated({ modules: ["Sale", "Order"] }), {
    notes: "a partner offer's OTP verify on the offer-shaped channel (`verifyChannel: \"offer\"`)",
  }),
  customerOfferUnblock: route<SaleBody, SaleAnswer>("POST", "/sale/customer/offer/unblock", gated({ modules: ["Sale", "Order", "Support"] }), {
    notes: "releases a partner coupon when it comes off the bill (`discounts/release.ts` › `releaseExternalCoupon`)",
  }),
  /** every active table of the branch with its open orders and whether it is free or occupied */
  resourceBranchCurrent: route<undefined, WireTableOccupancy[]>("GET", "/sale/resource/branch/current", gated({ modules: ["Sale", "Order", "Support"] }), {
    notes:
      "per active table: its open orders and Free or Occupied (query `branchId`, else the token's). It also carries at most one upcoming and one running booking, but their window is computed in India time whatever the branch's zone, so v2 reads occupancy only and marks reserved tables itself (plan decision 26; backend.md O4; sale_controller.js:2489-2600, sale_service.js:5488-5553)",
  }),
});
